<script setup lang="ts">
export interface LoanProviderItem {
  uuid: string
  partner: string
  name: string
  productTitle: string
  logo: StoryBlokImage
  sticker: string
  promotionTitle: string
  promotionText: string
  superTags: string
  loanCustomOrder: string
  loanPurpose: string
  loanAmountMin: string
  loanAmountMax: string
  loanTermMin: string
  loanTermMinUnit?: string
  loanTermMax: string
  loanTermMaxUnit: string
  showTermUnitOnly?: boolean
  loanInstalmentMin: string
  rating: string
  ratingTooltip: StoryBlokRichText
  interest: number | string
  rpsn: string
  availability: string
  loanApprovalFee: string
  loanManagementFee: string
  loanAmountChangeFee: string
  loanInstalmentChangeFee: string
  loanEarlyRedemptionFee: string
  loanInstalmentPenaltyTitle: string
  loanInstalmentPenalty: StoryBlokRichText
  tags?: object[]
  detailTitleConditions: string
  detailTextConditions: StoryBlokRichText
  detailTitleDocuments: string
  detailTextDocuments: StoryBlokRichText
  detailTitleAdvantages: string
  detailTextAdvantages: StoryBlokRichText
  detailTitleTerms: string
  detailTextTerms: StoryBlokRichText
  detailTitleAboutProvider: string
  detailTextAboutProvider: StoryBlokRichText
  detailTitleExample: string
  detailTextExample: StoryBlokRichText
  modalTitle?: string
  modalText?: StoryBlokRichText
  modalTitleSuccess?: string
  modalTextSuccess?: StoryBlokRichText
  modalTitleReject?: string
  modalTextReject?: StoryBlokRichText
  link: StoryBlokLink
  partnerLPLink: StoryBlokLink
  loanProviderInfoItems?: any
  loanAmount: number | string
  calcMonths: number
  loanLabels: StoryBlokLoanLabels
  payDayLoan?: boolean
  trackingHash?: string
  isDarkBackground?: boolean
  isExtendedVersion?: boolean
  isWidget?: boolean
  notAvailable?: boolean
  nAModalTitle?: string
  nAModal?: StoryBlokRichText
  nAModalContainerText?: StoryBlokRichText
  urlParams?: string
  isFrame?: boolean
  loanExpenseRating?: number
  delayExpenseRating?: number
  extraRatingInfo?: StoryBlokRichText
  qualityRating: number
}

const props = defineProps<LoanProviderItem>()
const emit = defineEmits(['openDetail', 'clickProvider'])
const appConfig = useAppConfig()
const openModal = ref(false)
const openNaModal = ref(false)
const open = ref(NaN)
const openQualityModal = ref(false)
function openItem(index: number) {
  open.value = index
}
function closeItem(index: number) {
  if (index === open.value) {
    open.value = NaN
    return
  }
  open.value = index
}
function numberFormatting(e: string) {
  return Number(e.replace(',', '.')).toLocaleString(appConfig.locale.code)
}
function percentFormatting(e: string) {
  return Number(e.replace(',', '.')).toFixed(2).replace('.', ',')
}
function loanTypeFormatting(e: string) {
  switch (e) {
    case 'bank': return props.loanLabels.bank
    case 'nobank': return props.loanLabels.noBank
  }
}

const hasRichtextContent = useHasContent
const details: Array<{ title: string; text: StoryBlokRichText }> = []
if (hasRichtextContent(props.detailTextConditions))
  details.push({ title: props.detailTitleConditions, text: props.detailTextConditions })
if (hasRichtextContent(props.detailTextDocuments))
  details.push({ title: props.detailTitleDocuments, text: props.detailTextDocuments })
if (hasRichtextContent(props.detailTextAdvantages))
  details.push({ title: props.detailTitleAdvantages, text: props.detailTextAdvantages })
if (hasRichtextContent(props.detailTextTerms))
  details.push({ title: props.detailTitleTerms, text: props.detailTextTerms })
if (hasRichtextContent(props.detailTextAboutProvider))
  details.push({ title: props.detailTitleAboutProvider, text: props.detailTextAboutProvider })
if (hasRichtextContent(props.detailTextExample))
  details.push({ title: props.detailTitleExample, text: props.detailTextExample })

function openDetail() {
  emit('openDetail', props.name, props.uuid, props.link.linktype === 'url' ? 'partner-clickout' : 'online')
  openModal.value = true
}

const countedLoanInstalment = computed(() => {
  if (!props.isWidget) {
    const number = Math.ceil((+props.loanAmount + (+props.loanAmount * (props.interest / 100))) / props.calcMonths)
    return numberFormatting(String(number))
  }
  else {
    return `${numberFormatting(props.loanAmountMin)} - ${numberFormatting(props.loanAmountMax)}`
  }
})

if (props.link.linktype === 'url' && props.urlParams) {
  const url = new URL(props.link.cached_url)
  const params = props.urlParams.split('&')
  for (const param of params) {
    const [key, value] = param.split('=')
    url.searchParams.append(key, value)
  }
  props.link.cached_url = url.toString()
  props.link.url = url.toString()
}
if (props.link && props.link.cached_url.includes('espoluprace') && props.trackingHash) {
  const url = new URL(props.link.cached_url)
  url.searchParams.append('aff_sub2', props.trackingHash)
  props.link.cached_url = url.toString()
  props.link.url = url.toString()
}
const enhancedLink = ref(props.link)

function clickProvider(isDetail: boolean) {
  if (props.notAvailable)
    openNaModal.value = true
  else if (!props.isWidget)
    emit('clickProvider', isDetail, props.partner, props.uuid, enhancedLink.value)
  else
    emit('clickProvider', props.partner, props.uuid, enhancedLink.value)
}
</script>

<template>
  <article class="flex flex-col w-full" :class="$attrs.class">
    <div
      class="grid grid-cols-2 items-center <xs:gap-1 <xs:px-1 xs:gap-2.5 xs:px-5 py-4 border border-gray-300 rounded-t-xl rounded-b-0 relative xl:flex xl:flex-row xl:items-center xl:gap-4"
      :class="
        {
          'rounded-b-xl': !promotionText || isWidget,
          'bg-white': isDarkBackground || !isWidget,
          'bg-pastel': !isDarkBackground && isWidget,
        }"
    >
      <div
        v-if="sticker && !isWidget"
        class="col-span-2 border border-success rounded-lg bg-success-pastel px-2.5 text-success self-start absolute z-10 -top-3 left-4 test-sm"
      >
        {{ sticker }}
      </div>
      <div
        :class="{
          'xl:flex-auto': isWidget && isExtendedVersion,
          'col-span-2 flex flex-row-reverse justify-between gap-2.5 xl:flex-row xl:items-center xl:gap-4': !isWidget || isExtendedVersion,
        }"
      >
        <div
          class="flex flex-col justify-center gap-2.5 w-140px xl:items-start"
          :class="isExtendedVersion || !isWidget ? 'items-end' : 'items-start'"
        >
          <StoryblokLink
            v-bind="isFrame ? null : partnerLPLink"
            :to="!isFrame && partnerLPLink ? partnerLPLink.cached_url : ''"
            class="cursor-pointer"
            @click="partnerLPLink ? navigateTo(partnerLPLink.cached_url) : null"
          >
            <StoryblokImage :src="logo" height="40" />
          </StoryblokLink>
        </div>
        <div
          v-if="isExtendedVersion || !isWidget"
          class="flex flex-col justify-center items-start xl:gap-2.5 xl:w-150px"
        >
          <StoryblokLink
            v-bind="isFrame ? null : partnerLPLink"
            :to="!isFrame && partnerLPLink ? partnerLPLink.cached_url : ''"
            class="cursor-pointer"
            @click="partnerLPLink ? navigateTo(partnerLPLink.cached_url) : null"
          >
            <div class="font-bold">
              {{ name }}
            </div>
          </StoryblokLink>
          <StoryblokLink
            v-bind="isFrame ? null : partnerLPLink"
            :to="!isFrame && partnerLPLink ? partnerLPLink.cached_url : ''"
            class="cursor-pointer"
            @click="partnerLPLink ? navigateTo(partnerLPLink.cached_url) : null"
          >
            <div>{{ productTitle }}</div>
          </StoryblokLink>
        </div>
      </div>
      <div v-if="!isWidget || isExtendedVersion" class="flex text-warm-gray-500 gap-2.5 items-center xl:hidden">
        <span>{{ loanLabels.rating }}</span>
        <BaseTooltip
          v-if="loanProviderInfoItems && hasRichtextContent(loanProviderInfoItems.rating)"
          :text="loanProviderInfoItems.rating"
          offset-skid="50"
        >
          <template #tooltipText>
            <AppIcon :size="13" icon="far-info-circle" class="text-warm-gray-500" />
          </template>
        </BaseTooltip>
      </div>
      <div class="justify-self-end text-center xl:w-116px" :class="{ 'xl:flex-auto': isWidget }">
        <QualityIndex class="cursor-pointer mx-auto" :value="Number(rating) / 10" @click="openQualityModal = true" />
      </div>
      <span v-if="!isWidget || isExtendedVersion" class="text-warm-gray-500 xl:hidden">
        {{ isWidget ? loanLabels.paymentNumber : loanLabels.loanFrom }}
      </span>
      <div
        class="xl:flex-auto text-center"
        :class="{
          'justify-self-end text-right w-100px': !isWidget,
          '<xl:flex <xl:flex-row <xl:justify-between <xl:w-full <xl:col-span-2': isWidget && !isExtendedVersion,
          'w-100px justify-self-end': isExtendedVersion,
        }"
      >
        <div>
          {{ `${countedLoanInstalment}&nbsp;${loanLabels.currency}` }}
        </div>
        <div v-if="!isExtendedVersion && isWidget">
          {{ `${loanTermMin}${loanTermMinUnit ? ` ${loanTermMinUnit}` : ''} - ${loanTermMax} ${loanTermMaxUnit}` }}
        </div>
      </div>
      <div v-if="!isWidget" class="flex items-center gap-2.5 text-warm-gray-500 xl:hidden">
        <span class="whitespace-nowrap">{{ loanLabels.aprcFrom }}</span>
        <BaseTooltip
          v-if="loanProviderInfoItems && hasRichtextContent(loanProviderInfoItems.rpsn)"
          :text="loanProviderInfoItems.rpsn"
          offset-skid="50"
        >
          <template #tooltipText>
            <AppIcon :size="13" icon="far-info-circle" class="mb-px text-warm-gray-500" />
          </template>
        </BaseTooltip>
      </div>
      <div
        v-if="!isWidget"
        class="w-96px text-right justify-self-end"
      >
        {{ percentFormatting(rpsn) }} %
      </div>
      <span v-if="!isWidget" class="text-warm-gray-500 xl:hidden">{{ loanLabels.interestFrom }}</span>
      <div v-if="!isWidget" class="w-92px text-right justify-self-end">
        <div class="font-semibold">
          {{ Number(interest).toFixed(2).replace('.', ',') }} %
        </div>
      </div>
      <span v-if="!isWidget || isExtendedVersion" class="text-warm-gray-500 xl:hidden">
        {{ loanLabels.loanMaturity }}
      </span>
      <div v-if="isExtendedVersion || !isWidget" class="w-140px justify-self-end text-right md:text-center" :class="{ 'xl:flex-auto': isWidget }">
        {{ showTermUnitOnly ? loanTermMaxUnit : `${loanTermMin}${loanTermMinUnit ? ` ${loanTermMinUnit}` : ''} - ${loanTermMax} ${loanTermMaxUnit}` }}
      </div>
      <div
        class="col-span-2 w-full flex flex-col gap-2.5 md:flex-row-reverse xl:flex-col xl:w-190px"
        :class="{ 'xl:flex-auto': isWidget }"
      >
        <BaseButton
          v-if="enhancedLink.linktype === 'url'"
          :name="`to-web-${partner}`" size="small" :text="loanLabels.actionButtonWebText || 'Přejít na web partnera'"
          :test-id="`to-web-${partner}`"
          class="md:w-1/2 xl:w-full cursor-pointer select-none"
          :class="{ 'md:w-full': isWidget && !isExtendedVersion }"
          @click="clickProvider(false)"
        />
        <BaseButton
          v-else
          :name="`to-form-${partner}`" size="small" :text="loanLabels.actionButtonFormText || 'Mám zájem'"
          class="md:w-1/2 xl:w-full" :test-id="`to-form-${partner}`"
          :class="{ 'md:w-full': isWidget && !isExtendedVersion }"
          external @click="clickProvider(false)"
        />
        <BaseButton
          v-if="isExtendedVersion || !isWidget"
          :name="`to-detail-${partner}`" size="small" render="secondary" text="Detail" class="md:w-1/2 xl:w-full" :test-id="`to-detail-${partner}`"
          @click.prevent="openDetail()"
        />
      </div>
    </div>
    <div
      v-if="(promotionTitle || promotionText) && !isWidget"
      class="flex flex-row w-full gap-5 items-center px-5 py-2.5 border border-success rounded-b-xl bg-success-pastel"
    >
      <div v-if="promotionTitle" class="bg-success rounded-lg py-0.5 px-2.5 text-white font-bold">
        {{ promotionTitle }}
      </div>
      <div v-if="promotionText">
        {{ promotionText }}
      </div>
    </div>
  </article>
  <PageLoanQualityModal
    v-model="openQualityModal"
    :loan-labels="loanLabels"
    :loan-expense-rating="loanExpenseRating"
    :delay-expense-rating="delayExpenseRating"
    :quality-rating="qualityRating"
    :extra-rating-info="extraRatingInfo"
  />
  <BaseModal v-if="notAvailable" v-model="openNaModal">
    <template #header>
      <h3>{{ nAModalTitle }}</h3>
    </template>
    <template #content>
      <StoryblokRichText class="my-3" :document="nAModal" />
      <div class="rounded-xl mb-3 bg-pastel border border-primary">
        <StoryblokRichText class="px-5 pt-5" :document="nAModalContainerText" />
      </div>
    </template>
    <template #footer>
      <div class="flex justify-center">
        <a class="cursor-pointer text-primary" @click="openNaModal = false">{{ loanLabels.backToOffers }}</a>
      </div>
    </template>
  </BaseModal>
  <BaseModal v-if="isExtendedVersion || !isWidget" v-model="openModal">
    <template #header>
      <div class="">
        <h2>
          {{ loanLabels.modalTitle }}
        </h2>
      </div>
    </template>
    <template #content>
      <div class="md:min-w-[540px]">
        <div class="flex flex-col justify-between mt-5 gap-2.5 md:flex-row">
          <div class="flex flex-row gap-3 justify-between md:flex-grow-1 md:flex-col">
            <div>
              <StoryblokLink
                v-bind="isFrame ? null : partnerLPLink"
                :to="!isFrame && partnerLPLink ? partnerLPLink.cached_url : ''"
                class="cursor-pointer"
                @click="partnerLPLink ? navigateTo(partnerLPLink.cached_url) : null"
              >
                <h3>{{ name }}</h3>
                <h4>{{ productTitle }}</h4>
              </StoryblokLink>
            </div>
            <StoryblokLink
              v-bind="isFrame ? null : partnerLPLink"
              :to="!isFrame && partnerLPLink ? partnerLPLink.cached_url : ''"
              class="cursor-pointer"
              @click="partnerLPLink ? navigateTo(partnerLPLink.cached_url) : null"
            >
              <StoryblokImage :src="logo" height="40" justify="left" class="my-2.5" />
            </StoryblokLink>
          </div>
          <div class="flex flex-col min-w-55">
            <BaseButton
              v-if="enhancedLink.linktype === 'url'"
              :name="`to-web-${partner}-in-detail`" :text="loanLabels.actionButtonwebText || 'Přejít na web partnera'" class="hover:cursor-pointer select-none" :test-id="`to-web-${partner}-in-detail`"
              @click="clickProvider(true)"
            />
            <BaseButton
              v-else
              :name="`to-form-${partner}-in-detail`" :text="loanLabels.actionButtonFormText || 'Mám zájem'" class="hover:cursor-pointer" :test-id="`to-form-${partner}-in-detail`"
              :to="enhancedLink.cached_url" external @click="clickProvider(true)"
            />
          </div>
        </div>
        <div class="flex flex-col rounded-xl bg-gray-300 bg-opacity-20 p-5 mt-2.5">
          <div class="text-lg font-bold">
            {{ loanLabels.basicInfoTitle }}
          </div>
          <div class="py-2.5 flex justify-between">
            <div class="flex gap-3 items-center">
              <span class="whitespace-nowrap">{{ loanLabels.rating }}</span>
              <BaseTooltip
                v-if="loanProviderInfoItems && hasRichtextContent(loanProviderInfoItems.rating)"
                :text="loanProviderInfoItems.rating"
              >
                <template #tooltipText>
                  <div class="flex items-center text-secondary">
                    <AppIcon :size="16" icon="far-info-circle" class="mb-px" />
                  </div>
                </template>
              </BaseTooltip>
            </div>
            <QualityIndex :value="Number(rating) / 10" />
          </div>
          <hr>
          <div class="py-2.5 flex justify-between">
            <div>{{ loanLabels.loanTypes }}</div>
            <div class="font-bold">
              {{ loanTypeFormatting(superTags) }}
            </div>
          </div>
          <hr>
          <div v-if="loanInstalmentMin" class="py-2.5 flex justify-between">
            <div>{{ loanLabels.loanFrom }}</div>
            <div class="font-bold">
              {{ numberFormatting(loanInstalmentMin) }} {{ loanLabels.currency }}
            </div>
          </div>
          <hr>
          <div v-if="interest" class="py-2.5 flex justify-between">
            <div>{{ loanLabels.interestFrom }}</div>
            <div class="font-bold">
              {{ Number(interest) ? Number(interest).toFixed(2).replace('.', ',') : interest }} %
            </div>
          </div>
          <hr>
          <div class="py-2.5 flex justify-between">
            <div>{{ loanLabels.loanMax }}</div>
            <div class="font-bold">
              {{ numberFormatting(loanAmountMax) }} {{ loanLabels.currency }}
            </div>
          </div>
          <hr>
          <div class="py-2.5 flex justify-between">
            <div class="flex gap-3">
              <span class="whitespace-nowrap">{{ loanLabels.aprcFrom }}</span>
              <BaseTooltip
                v-if="loanProviderInfoItems && loanProviderInfoItems.rpsn" :text="loanProviderInfoItems.rpsn"
              >
                <template #tooltipText>
                  <div class="flex items-center text-secondary">
                    <AppIcon :size="16" icon="far-info-circle" class="mb-px" />
                  </div>
                </template>
              </BaseTooltip>
            </div>
            <div class="font-bold">
              {{ percentFormatting(rpsn) }} %
            </div>
          </div>
          <hr>
          <div class="pt-2.5 flex justify-between">
            <div class="flex">
              <span class="whitespace-nowrap">{{ loanLabels.finishOnline }}</span>
              <BaseTooltip
                v-if="loanProviderInfoItems && hasRichtextContent(loanProviderInfoItems.availability)" :text="loanProviderInfoItems.availability"
                offset-skid="50" color="text-gray-500"
              />
            </div>
            <div class="font-bold text-success">
              {{ availability }}
            </div>
          </div>
        </div>
        <hr class="mt-5">
        <div
          v-for="(detail, index) in details" v-editable="detail" class="mt-5"
          @click="index === open ? closeItem(index) : openItem(index)"
        >
          <div class="flex items-center justify-between">
            <h3>{{ detail.title }}</h3>
            <AppIcon
              size="24" msize="20" :class="[index === open && 'rotate-180']"
              class="text-primary mr-2 duration-300 transition transform" icon="far-angle-down"
              @click.stop="closeItem(index)"
            />
          </div>
          <div v-if="index === open" class="mt-3 transition duration-700 transform scale-x-100 text-large">
            <StoryblokRichText :document="detail.text" />
          </div>
        </div>
      </div>
    </template>
  </BaseModal>
</template>
